import type {
  FilterOption,
  IntegrationWithEnvironments,
  DbtModelFiltersResponse,
} from "@/types/dbtModels";

// project.value format: `dbt://{integration_id}.{env_id}/{project_name}`
const parseProjectCompositeKey = (value: string): string | null => {
  const match = value.match(/^dbt:\/\/([^/]+)\//);
  return match?.[1] ?? null;
};

const buildEnvLabelByCompositeKey = (
  groups: IntegrationWithEnvironments[]
): Record<string, string> => {
  const map: Record<string, string> = {};
  groups.forEach(g => {
    g.environments.forEach(env => {
      map[`${g.integration_value}.${env.value}`] = env.label;
    });
  });
  return map;
};

/**
 * Disambiguate projects that share the same label by suffixing the environment name.
 * Projects can repeat across environments (same project name, different env), so a bare
 * label like "HDCL_DV2" is ambiguous; we append "(Production_5505)" etc. for those cases.
 */
export const disambiguateProjects = (
  projects: FilterOption[],
  integrationsWithEnvironments: DbtModelFiltersResponse["integrations_with_environments"]
): FilterOption[] => {
  const labelCounts: Record<string, number> = {};
  projects.forEach(p => {
    labelCounts[p.label] = (labelCounts[p.label] || 0) + 1;
  });

  const envByCompositeKey = buildEnvLabelByCompositeKey(integrationsWithEnvironments ?? []);

  return projects.map(p => {
    if (labelCounts[p.label] <= 1) return p;
    const compositeKey = parseProjectCompositeKey(p.value);
    const envLabel = compositeKey ? envByCompositeKey[compositeKey] : null;
    // Happy path: env label from integrations_with_environments. Fallback to the composite key
    // (or the raw value) so duplicate-labeled projects are never visually identical, even when
    // the env map is partial or the value doesn't parse.
    return {
      ...p,
      label: `${p.label} (${envLabel ?? compositeKey ?? p.value})`,
    };
  });
};
